import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ECollection } from '../enums/ECollection';
import { EModelApiPaths } from '../enums/EModelApiPaths';
import { IQueryFormat } from '../enums/types';
import { ITickets, ITickets_P, ITickets_S } from '../interfaces/ITickets';
import { AuthService } from './auth.service';
import { BaseService } from './base-service';
import { UserService } from './user.service';
import { UtilsService } from './utils.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TicketsService extends BaseService {
    constructor(
        public override http: HttpClient,
        public override utilsService: UtilsService,
        public override userService: UserService,
        public authService: AuthService
    ) {
        super(http, userService, utilsService, `/schema/${environment.USER_PATH}/${environment.INS}/${environment.DB}/${ECollection.SW_TICKETS}`);
    }

    /**
     * Create a new ticket
     */
    async createTicket(ticketData: any): Promise<ITickets> {
        return await this.post<ITickets>(ticketData);
    }

    /**
     * Get ticket by order ID (check if issue exists)
     */
    async getTicketByOrderId(orderId: string): Promise<ITickets | null> {
        const query = <IQueryFormat>{
            find: { order_id: orderId },
            sort: { createdAt: -1 },
            limit: 1
        };
        const result = await this.query<ITickets[]>(query);
        return result && result.length > 0 ? result[0] : null;
    }

    /**
     * Get ticket by ID
     */
    async getTicketById(ticketId: string): Promise<ITickets> {
        return await this.getById<ITickets>(ticketId);
    }

    /**
     * Get ticket by ID with user information (deep query)
     */
    async getTicketByIdWithUser(ticketId: string): Promise<ITickets> {
        return await this.getByIdAndDeep<ITickets>(ticketId, JSON.stringify(
            [
                {
                    s_key: 'ord_user_id',
                    t_instance: environment.INS,
                    t_db: environment.DB,
                    t_col: ECollection.SW_USERS,
                    t_key: '_id',
                    select: {
                        usr_first_name_str: 1,
                        usr_last_name_str: 1,
                        usr_email_str: 1,
                        usr_mobile_number_num: 1
                    }
                }
            ]
        ));
    }

    /**
     * Update ticket status (open, resolved, closed)
     */
    async updateTicketStatus(ticketId: string, status: string): Promise<ITickets> {
        const updateData: ITickets_P = {
            status: status
        };
        return await this.put<ITickets>(ticketId, updateData);
    }

    /**
     * Get all tickets for a user
     */
    async getTicketsByUserId(userId: string, select?: ITickets_S): Promise<ITickets[]> {
        const query = <IQueryFormat>{
            find: { user_id: userId, active: true },
            select: select || {
                issue_id: 1,
                order_id: 1,
                status: 1,
                subject: 1,
                createdAt: 1,
            },
            sort: { createdAt: -1 }
        };
        return await this.query<ITickets[]>(query);
    }

    /**
     * Soft delete a ticket
     */
    async closeTicket(ticketId: string): Promise<ITickets> {
        return await this.softDelete<ITickets>(ticketId);
    }
}
